import type { FormPromoFields } from 'api/types/addresses.types';
import { GreenCheckIcon } from 'assets/icons';
import Button from 'components/atoms/Button';
import { BUTTON_NAMES } from 'constant/button-names';
import { PromoFields } from 'packages/shared/checkout/constant';
import { usePromoCode } from 'packages/shared/checkout/hooks/usePromoCode';
import type { IPromoField } from 'packages/shared/checkout/types';
import React, { useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import type { Control, FieldError } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const StyledControllerWrapper = styled.div<{ applied?: boolean; isError?: boolean }>`
  flex: 1;
  position: relative;
  padding-right: 3%;
  padding-left: 3%;
  background-color: ${({ theme, isError, applied }) =>
    isError ? `${theme.colors.warning}20` : applied ? theme.colors.mintGreen : theme.colors.white};
`;

const StyledControl = styled(Form.Control)`
  border: transparent 0px solid;
  background-color: transparent;
  font-size: 14px;
  font-weight: 400;

  &:disabled,
  &:focus {
    background-color: transparent;
  }
`;

const StyledTextSuccesss = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
`;

const StyledApplyButton = styled(Button)<{ applied?: boolean; isError?: boolean }>`
  padding: 7px 15px;
  border-radius: 54px;
  height: 36px;
  border: none;
  cursor: ${({ applied }) => (applied ? 'default' : 'pointer')};
  align-items: center;
  background-color: ${({ theme, applied }) => (applied ? theme.colors.darkGreen : theme.colors.deepPurple)};
`;

const StyledText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.4px;
`;

const StyledRow = styled.div<{ isError?: boolean; isSuccess?: boolean }>`
  flex: 1;
  flex-shrink: 0;
  border-radius: 54px;
  border: 1px solid
    ${({ theme, isError, isSuccess }) => (isError ? theme.colors.warningYellow : isSuccess ? theme.colors.brightGreen : theme.colors.staleBlue)};
  display: flex;
  justify-content: space-between;
  overflow: hidden;
`;

const StyledRowWrapper = styled.div<{ isError?: FieldError }>`
  margin-top: 16px;
  ${({ isError }) =>
    isError &&
    css`
      ${StyledRow} {
        border-color: ${({ theme }) => theme.colors.warningYellow};
      }
    `}
`;

const StyledItemsRow = styled(Row)`
  gap: 8px;
`;

const StyledH2 = styled.h2`
  color: ${({ theme }) => theme.text.color};
  font-weight: 600;
  font-size: 16px;
  line-height: 21px;
  margin-bottom: 0px;
`;

const StyledGreenCheckIcon = styled(GreenCheckIcon)`
  margin-left: 5px;
  margin-top: -2px;
`;

const InputField: React.FC<IInstaShopInputProps> = ({ field, control, error, successMessage, applied }) => {
  const { t } = useTranslation();
  return (
    <StyledControllerWrapper isError={Boolean(error)} applied={applied}>
      <Controller
        name={field.name}
        control={control}
        rules={{ required: field.required }}
        render={({ ...currentField }) => (
          <StyledControl
            {...currentField}
            placeholder={t('common.addPromoCode', 'Add promocode')}
            isInvalid={Boolean(error)}
            disabled={!!successMessage}
            applied={applied}
          />
        )}
      />
    </StyledControllerWrapper>
  );
};

export interface IInstaShopInputProps {
  control: Control<any>;
  error: FieldError;
  successMessage?: string;
  field: FormPromoFields;
  applied?: boolean;
}

const DefaultPromoField: React.FC<IPromoField> = ({ onClose, show }) => {
  const { t } = useTranslation();

  const { handleSubmit, submitHandler, errors, control, successMessage, isLoading } = usePromoCode({
    onClose,
    show,
  });

  const buttonTitle = useMemo(() => (successMessage ? t('common.remove', 'Applied') : t('common.apply', 'Apply')), [successMessage, t]);

  return (
    <StyledRowWrapper isError={errors[PromoFields.name as 'promocode']}>
      <StyledH2 className="mr-4">{t('newOrderPage.promoCodeText', 'Promocode')}</StyledH2>
      <StyledItemsRow noGutters className="d-flex mb-5 mt-3 align-items-center justify-content-between w-100">
        <StyledRow isSuccess={Boolean(successMessage)} isError={Boolean(errors[PromoFields.name as 'promocode'])}>
          <InputField
            successMessage={successMessage}
            control={control}
            field={PromoFields}
            error={errors[PromoFields.name as 'promocode']!!}
            applied={!!successMessage}
          />
        </StyledRow>
        <StyledApplyButton
          block={false}
          disabled={!!successMessage}
          applied={!!successMessage}
          isError={Boolean(errors[PromoFields.name as 'promocode'])}
          onClick={handleSubmit(submitHandler)}
          isLoading={isLoading}
          buttonName={BUTTON_NAMES.PROMO_APPLY}
        >
          {!successMessage ? <StyledText>{buttonTitle}</StyledText> : <StyledTextSuccesss>{buttonTitle}</StyledTextSuccesss>}
          {Boolean(successMessage) && <StyledGreenCheckIcon />}
        </StyledApplyButton>
      </StyledItemsRow>
    </StyledRowWrapper>
  );
};

export default DefaultPromoField;
